import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  Heading,
  Skeleton,
  Text,
} from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import useTrainingPerformanceStore from 'components/sections/admin-portal/machines/dialogs/training-performance/store/use-training-performance-store';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ITrainingLogSummary } from 'lib_ts/interfaces/training/i-training-log';
import { ReactNode, useMemo } from 'react';

const ValueWithUnit = (props: {
  value: number;
  decimals: number;
  unit: string;
}) => {
  if (isNaN(props.value)) {
    return <Text color={RADIX.COLOR.SECONDARY}>N/A</Text>;
  }

  return (
    <>
      <Text>{props.value.toFixed(props.decimals)}</Text>&nbsp;
      <Text color={RADIX.COLOR.SECONDARY}>{props.unit}</Text>
    </>
  );
};

const CustomRow = (props: { label: string; value: ReactNode }) => (
  <Flex justify="between">
    <Box>
      <Text color={RADIX.COLOR.SECONDARY}>{props.label}</Text>
    </Box>
    <Box>{props.value}</Box>
  </Flex>
);

export const TrainingPerfMetrics = () => {
  const loading = useTrainingPerformanceStore((s) => s.loading);
  const logs = useTrainingPerformanceStore((s) => s.logs);

  const summary: ITrainingLogSummary = useMemo(() => {
    if (logs.length === 0) {
      const empty: ITrainingLogSummary = {
        ptRate: 0,
        ptSuccessRate: 0,
        ptSmartSamplingRate: 0,
        meanPtTrainingThreshold: 0,
        meanPtRelThresholds: 0,
        meanPtSuccessIterations: 0,
        meanPtSuccessShots: 0,
        meanPtSuccessExcessShots: 0,
        maeVMph: 0,
        maePlateXIn: 0,
        maePlateZIn: 0,
        maeWXRpm: 0,
        maeWYRpm: 0,
        maeWZRpm: 0,
        maeBreakXIn: 0,
        maeBreakZIn: 0,
        machineID: '',
      };
      return empty;
    }

    return TrainingHelper.summarizeLogs(logs);
  }, [logs]);

  return (
    <ErrorBoundary identifier="TrainingPerfMetrics">
      <Grid columns={{ xs: '1', lg: '3' }} gap="4">
        <Box>
          <Heading size="3" mb="2" truncate>
            Training Info
          </Heading>
          <Skeleton loading={loading}>
            <Card size="2" style={{ height: '140px' }}>
              <Flex direction="column" gap="2">
                <CustomRow
                  label="PT Percentage"
                  value={`${(summary.ptRate * 100).toFixed(0)}%`}
                />
                <CustomRow
                  label="Mean PT Training Shots Setting"
                  value={summary.meanPtTrainingThreshold.toFixed(1)}
                />
                <CustomRow
                  label="PT With Smart Sampling"
                  value={`${(summary.ptSmartSamplingRate * 100).toFixed(0)}%`}
                />
                <CustomRow
                  label="Mean PT Relative Thresholds"
                  value={summary.meanPtRelThresholds.toFixed(2)}
                />
              </Flex>
            </Card>
          </Skeleton>
        </Box>
        <Box>
          <Heading size="3" mb="2" truncate>
            Precision Train Performance
          </Heading>
          <Skeleton loading={loading}>
            <Card size="2" style={{ height: '140px' }}>
              <Flex direction="column" gap="2">
                <CustomRow
                  label="Success Rate"
                  value={
                    <Badge
                      color={
                        summary.ptSuccessRate >= 0.9
                          ? RADIX.COLOR.SUCCESS
                          : summary.ptSuccessRate >= 0.8
                            ? RADIX.COLOR.WARNING
                            : RADIX.COLOR.DANGER
                      }
                    >
                      {(summary.ptSuccessRate * 100).toFixed(0)}%
                    </Badge>
                  }
                />
                <CustomRow
                  label="Mean Adjustments"
                  value={(summary.meanPtSuccessIterations - 1).toFixed(1)}
                />
                <CustomRow
                  label="Mean Shots"
                  value={summary.meanPtSuccessShots.toFixed(2)}
                />
                <CustomRow
                  label="Mean Excess Shots"
                  value={summary.meanPtSuccessExcessShots.toFixed(2)}
                />
              </Flex>
            </Card>
          </Skeleton>
        </Box>
        <Box>
          <Heading size="3" mb="2" truncate>
            First Guess Mean Error
          </Heading>
          <Skeleton loading={loading}>
            <Card size="2" style={{ height: '140px' }}>
              <Grid columns="2" gap="6">
                <Flex direction="column" gap="2">
                  <CustomRow
                    label="Speed"
                    value={
                      <ValueWithUnit
                        value={summary.maeVMph}
                        decimals={1}
                        unit="mph"
                      />
                    }
                  />
                  <CustomRow
                    label="Spin X"
                    value={
                      <ValueWithUnit
                        value={summary.maeWXRpm}
                        decimals={1}
                        unit="rpm"
                      />
                    }
                  />
                  <CustomRow
                    label="Spin Y"
                    value={
                      <ValueWithUnit
                        value={summary.maeWYRpm}
                        decimals={1}
                        unit="rpm"
                      />
                    }
                  />
                  <CustomRow
                    label="Spin Z"
                    value={
                      <ValueWithUnit
                        value={summary.maeWZRpm}
                        decimals={1}
                        unit="rpm"
                      />
                    }
                  />
                </Flex>
                <Flex direction="column" gap="2">
                  <CustomRow
                    label="Plate X"
                    value={
                      <ValueWithUnit
                        value={summary.maePlateXIn}
                        decimals={1}
                        unit="in"
                      />
                    }
                  />
                  <CustomRow
                    label="Plate Z"
                    value={
                      <ValueWithUnit
                        value={summary.maePlateZIn}
                        decimals={1}
                        unit="in"
                      />
                    }
                  />
                  <CustomRow
                    label="Break X"
                    value={
                      <ValueWithUnit
                        value={summary.maeBreakXIn}
                        decimals={1}
                        unit="in"
                      />
                    }
                  />
                  <CustomRow
                    label="Break Z"
                    value={
                      <ValueWithUnit
                        value={summary.maeBreakZIn}
                        decimals={1}
                        unit="in"
                      />
                    }
                  />
                </Flex>
              </Grid>
            </Card>
          </Skeleton>
        </Box>
      </Grid>
    </ErrorBoundary>
  );
};
